// GENERATED FILE — DO NOT EDIT BY HAND. Produced by scripts/sync_learning_blocks.py from shared/learning-blocks/. Change the source there and re-run; CI fails on drift.
// Source: shared/learning-blocks/aid-style.ts

/**
 * Styling der interaktiven Lernhilfen — EINE Quelle für chat-widget und Portal.
 * Kopiert von `scripts/sync_learning_blocks.py` nach
 *   services/chat-widget/src/app/components/learning-block/aid-style.ts
 *   services/client-portal/src/app/pages/chat-styling/aid-style.ts
 * (CI prüft die Kopien) — so zeigt die Vorschau im Chat-Styling genau die
 * Farben, die das Widget rechnet. Nur hier ändern, dann das Skript laufen lassen.
 *
 * Drei Einstellungen im Chat-Styling (Marcel, 20.10.2026): Hintergrund,
 * Textfarbe und Highlight-Farbe. Alle übrigen Töne (Boxen, Buttons, Regler,
 * Linien, Nebentext, Icons) werden daraus berechnet und sind nicht einzeln
 * einstellbar.
 *
 * Grundregel „automatisch, bis überschrieben“:
 *   - ohne eigenen Wert gilt: Hintergrund weiß, Text Alphalearn-Dunkelblau,
 *     Highlight = Primärfarbe des Chat-Stylings;
 *   - ein gesetzter Wert gilt so, wie er ist — er springt nie zurück und folgt
 *     der Primärfarbe nicht mehr. Gespeichert werden nur gesetzte Werte;
 *     „Automatisch“ im Portal löscht den Schlüssel wieder.
 * Abgeleitete Töne sind kontrastgesichert (Schrift ≥ 4,5:1, Icons ≥ 3:1);
 * Hintergrund + Textfarbe nicht, wenn beide gesetzt — dafür warnt das Portal
 * (`aidContrastIssues`).
 *
 * Keine Abhängigkeiten, kein Angular — reine Funktionen.
 */

/** Was im Chat-Styling einstellbar ist (gespeicherte Schlüssel). */
export type AidStyleKey = 'aidCardBackground' | 'aidTextColor' | 'aidHighlightColor';

/** Alle berechneten Töne — die drei Einstellungen plus die Ableitungen. */
export type AidToken =
  | AidStyleKey
  | 'aidMutedColor'
  | 'aidCornerRadius'
  | 'aidBoxBackground'
  | 'aidBoxActiveBackground'
  | 'aidBoxActiveBorder'
  | 'aidButtonBackground'
  | 'aidButtonTextColor'
  | 'aidButtonInactiveBackground'
  | 'aidButtonInactiveTextColor'
  | 'aidAccentColor'
  | 'aidIconColor'
  | 'aidSliderTrack'
  | 'aidResultColor'
  | 'aidLineColor';

/** Lade- und Reveal-Animation der Lernhilfen. */
export type AidLoaderStyle = 'code-orbi' | 'pixel-glitter';
export const AID_LOADER_STYLES: readonly AidLoaderStyle[] = ['code-orbi', 'pixel-glitter'];
export const DEFAULT_AID_LOADER: AidLoaderStyle = 'code-orbi';

export interface AidStyleField {
  key: AidStyleKey;
  /** `color` = Vollton (Hex) · `fill` = Fläche, Verlauf erlaubt */
  kind: 'color' | 'fill';
}

/** Reihenfolge = Reihenfolge im Portal-Formular. */
export const AID_STYLE_FIELDS: readonly AidStyleField[] = [
  { key: 'aidCardBackground', kind: 'fill' },
  { key: 'aidTextColor', kind: 'color' },
  { key: 'aidHighlightColor', kind: 'fill' },
];

/** CSS-Variable je Ton im Widget (auf `app-learning-block`). */
export const AID_TOKEN_VARS: Readonly<Record<AidToken, string>> = {
  aidCardBackground: '--aid-card-bg',
  aidTextColor: '--aid-text',
  aidHighlightColor: '--aid-highlight',
  aidMutedColor: '--aid-muted',
  aidCornerRadius: '--aid-radius',
  aidBoxBackground: '--aid-box-bg',
  aidBoxActiveBackground: '--aid-box-active-bg',
  aidBoxActiveBorder: '--aid-box-active-border',
  aidButtonBackground: '--aid-button-bg',
  aidButtonTextColor: '--aid-button-text',
  aidButtonInactiveBackground: '--aid-button-inactive-bg',
  aidButtonInactiveTextColor: '--aid-button-inactive-text',
  aidAccentColor: '--aid-accent',
  aidIconColor: '--aid-icon',
  aidSliderTrack: '--aid-slider-track',
  aidResultColor: '--aid-result',
  aidLineColor: '--aid-line',
};

export const AID_STYLE_KEYS: readonly string[] = [...AID_STYLE_FIELDS.map((f) => f.key), 'aidLoader'];

/** Die Eingaben: Primärfarbe des Chat-Stylings plus gesetzte `aid*`-Werte. */
export type AidStyleInput = { primaryColor?: unknown } & {
  [key: string]: unknown;
};

export interface ResolvedAidStyle {
  /** Endgültiger CSS-Wert je Ton (gesetzt oder abgeleitet). */
  values: Record<AidToken, string>;
  /** Je Einstellung: true = abgeleitet („Auto“), false = im Styling gesetzt. */
  auto: Record<AidStyleKey, boolean>;
  loader: AidLoaderStyle;
}

// ---------------------------------------------------------------------------
// Farben
// ---------------------------------------------------------------------------

type Rgb = [number, number, number];

const DEFAULT_PRIMARY: Rgb = [0x00, 0x35, 0x98];
const WHITE: Rgb = [255, 255, 255];
const DARK: Rgb = [17, 24, 39];
/** Alphalearn-CI: Text. */
const TEXT: Rgb = [0x24, 0x36, 0x50];
const DEFAULT_RADIUS = 20;

/** `#rgb`, `#rrggbb`, `#rrggbbaa` (Alpha wird ignoriert), `#` darf fehlen. */
export function parseHex(value: unknown): Rgb | null {
  if (typeof value !== 'string') return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value.trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1].slice(0, 6);
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
}

/** Vollton oder erste Stopp-Farbe eines CSS-Verlaufs. */
export function firstColor(value: unknown): Rgb | null {
  const solid = parseHex(value);
  if (solid) return solid;
  if (typeof value !== 'string' || !/gradient\(/i.test(value)) return null;
  const stop = /#([0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{3})\b/i.exec(value);
  return stop ? parseHex(stop[0]) : null;
}

function isGradient(value: unknown): value is string {
  return typeof value === 'string' && /^(repeating-)?(linear|radial|conic)-gradient\(/i.test(value.trim()) && !!firstColor(value);
}

function luminance(c: Rgb): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** `a` mit Anteil `t` über `b` (t = 0.07 → 7 % a auf b). */
function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [0, 1, 2].map((i) => b[i] + (a[i] - b[i]) * t) as Rgb;
}

export function toHex(c: Rgb): string {
  return '#' + c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');
}

/** So weit Richtung Schwarz, bis `c` auf `bg` mindestens `min` erreicht. */
function inkOn(c: Rgb, bg: Rgb, min: number): Rgb {
  let out = c;
  for (let t = 0.04; contrastRatio(out, bg) < min && t <= 1; t += 0.04) out = mix(DARK, c, t);
  return out;
}

/** Weiß oder Dunkel — was auf `bg` mehr Kontrast hat. */
function onColor(bg: Rgb): Rgb {
  return contrastRatio(WHITE, bg) >= contrastRatio(DARK, bg) ? WHITE : DARK;
}

// ---------------------------------------------------------------------------
// Ableitung
// ---------------------------------------------------------------------------

/** Gesetzter Wert, wenn gültig für seine Art — sonst null (gilt als „Auto“). */
function explicitValue(field: AidStyleField, raw: unknown): string | null {
  if (raw === undefined || raw === null || raw === '') return null;
  if (field.kind === 'fill' && isGradient(raw)) return (raw as string).trim();
  const hex = parseHex(raw);
  return hex ? toHex(hex) : null;
}

/** `fg` so weit zum Hintergrund mischen, wie die Lesbarkeit (`min`) es zulässt. */
function softenOn(fg: Rgb, bg: Rgb, t: number, min: number): Rgb {
  for (let k = t; k < 1; k += 0.05) {
    const c = mix(fg, bg, k);
    if (contrastRatio(c, bg) >= min) return c;
  }
  return fg;
}

/** Alle Töne für ein Chat-Styling: gesetzte Einstellungen gelten, der Rest wird abgeleitet. */
export function resolveAidStyle(input: AidStyleInput): ResolvedAidStyle {
  const values = {} as Record<AidToken, string>;
  const auto = {} as Record<AidStyleKey, boolean>;
  const set = (key: AidStyleKey, derived: string): void => {
    const field = AID_STYLE_FIELDS.find((f) => f.key === key)!;
    const own = explicitValue(field, input[key]);
    values[key] = own ?? derived;
    auto[key] = own === null;
  };

  // Die drei Einstellungen
  set('aidCardBackground', '#ffffff');
  const card = firstColor(values.aidCardBackground) ?? WHITE;
  set('aidTextColor', toHex(contrastRatio(TEXT, card) >= 4.5 ? TEXT : onColor(card)));
  const text = firstColor(values.aidTextColor) ?? TEXT;
  const primaryRaw = input.primaryColor;
  set('aidHighlightColor', isGradient(primaryRaw) ? (primaryRaw as string).trim() : toHex(firstColor(primaryRaw) ?? DEFAULT_PRIMARY));
  const H = firstColor(values.aidHighlightColor) ?? DEFAULT_PRIMARY;

  // Karte
  values.aidMutedColor = toHex(softenOn(text, card, 0.3, 4.5));
  values.aidCornerRadius = `${DEFAULT_RADIUS}px`;

  // Boxen — Highlight als Tönung
  values.aidBoxBackground = toHex(mix(H, card, 0.07));
  values.aidBoxActiveBackground = toHex(mix(H, card, 0.16));
  values.aidBoxActiveBorder = toHex(mix(H, card, 0.45));

  // Buttons — Highlight (Verlauf bleibt Verlauf)
  values.aidButtonBackground = values.aidHighlightColor;
  values.aidButtonTextColor = toHex(onColor(H));
  const inactiveBg = mix(H, card, 0.22);
  values.aidButtonInactiveBackground = toHex(inactiveBg);
  values.aidButtonInactiveTextColor = toHex(mix(onColor(inactiveBg), inactiveBg, 0.55));

  // Akzent — Highlight als Schrift und Icon (kontrastgesichert)
  values.aidAccentColor = toHex(inkOn(H, card, 4.5));
  values.aidIconColor = toHex(inkOn(H, mix(H, card, 0.07), 3));

  // Rechner — Ergebnis in Textfarbe
  values.aidSliderTrack = toHex(mix(H, card, 0.2));
  values.aidResultColor = values.aidTextColor;

  // Linien
  values.aidLineColor = toHex(mix(text, card, 0.45));

  const loader = AID_LOADER_STYLES.includes(input['aidLoader'] as AidLoaderStyle)
    ? (input['aidLoader'] as AidLoaderStyle)
    : DEFAULT_AID_LOADER;
  return { values, auto, loader };
}

/** CSS-Variablen für das Widget (und die Portal-Vorschau). */
export function aidCssVariables(resolved: ResolvedAidStyle): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [token, cssVar] of Object.entries(AID_TOKEN_VARS)) out[cssVar] = resolved.values[token as AidToken];
  return out;
}

export interface AidContrastIssue {
  key: AidStyleKey;
  on: AidStyleKey;
  ratio: number;
  min: number;
}

/** Nur Text auf Hintergrund kann unlesbar werden — alle anderen Töne sind
 *  abgeleitet und gesichert. Gemeldet nur, wenn eins von beiden gesetzt ist. */
export function aidContrastIssues(resolved: ResolvedAidStyle): AidContrastIssue[] {
  if (resolved.auto.aidTextColor && resolved.auto.aidCardBackground) return [];
  const a = firstColor(resolved.values.aidTextColor);
  const b = firstColor(resolved.values.aidCardBackground);
  if (!a || !b) return [];
  const ratio = contrastRatio(a, b);
  return ratio < 4.5 ? [{ key: 'aidTextColor', on: 'aidCardBackground', ratio: Math.round(ratio * 10) / 10, min: 4.5 }] : [];
}
